import { useCallback, useEffect, useRef, useState } from 'react';
import request from 'lib/request';

interface ScenarioNameEditor {
  savedName: string;
  editedName: string;
  changeName: (name: string) => void;
  saveName: (name: string) => void;
  abandonEdit: () => void;
  savingName: boolean;
  nameError: string | null;
  dismissNameError: () => void;
}

/**
 * Owns the DCA scenario name shown in the page header: an edit buffer, the last
 * value confirmed by the server, and the PATCH that moves one to the other.
 *
 * Only an explicit save persists. Leaving the field throws the edit away, so
 * `abandonEdit` runs a frame late: on Enter the input blurs before it saves, and
 * the deferral lets that save claim the edit before the revert would discard it.
 *
 * @param {string} scenarioHref - Resource path to PATCH, e.g. `/dca_scenarios/14`.
 * @param {string} initialName - Name as last loaded from the server.
 * @return {ScenarioNameEditor} Name state and the edit/save handlers.
 */
export const useScenarioNameEditor = (
  scenarioHref: string,
  initialName: string
): ScenarioNameEditor => {
  const [savedName, setSavedName] = useState(initialName);
  const [editedName, setEditedName] = useState(initialName);
  const [nameError, setNameError] = useState<string | null>(null);
  const [savingName, setSavingName] = useState(false);
  const savedThisEdit = useRef(false);

  useEffect(() => {
    setSavedName(initialName);
    setEditedName(initialName);
  }, [initialName]);

  const changeName = useCallback((name: string) => {
    savedThisEdit.current = false;
    setEditedName(name);
  }, []);

  const saveName = useCallback(
    (newName: string) => {
      savedThisEdit.current = true;

      const trimmed = newName.trim();

      if (!trimmed || trimmed === savedName) {
        setEditedName(savedName);
        setNameError(null);
        return;
      }

      void (async () => {
        setSavingName(true);
        setNameError(null);

        const res = await request.patch<unknown, unknown>(
          scenarioHref,
          { dcaScenario: { name: trimmed } },
          new AbortController()
        );

        setSavingName(false);

        if (res.type === 'success') {
          setSavedName(trimmed);
          setEditedName(trimmed);
          return;
        }

        setEditedName(savedName);
        setNameError(errorMessage(res));
      })();
    },
    [scenarioHref, savedName]
  );

  const abandonEdit = useCallback(() => {
    requestAnimationFrame(() => {
      if (savedThisEdit.current) {
        savedThisEdit.current = false;
        return;
      }
      setEditedName(savedName);
    });
  }, [savedName]);

  const dismissNameError = useCallback(() => setNameError(null), []);

  return {
    savedName,
    editedName,
    changeName,
    saveName,
    abandonEdit,
    savingName,
    nameError,
    dismissNameError,
  };
};

const FALLBACK_ERROR = 'Could not update DCA scenario name';

const errorMessage = (res: { type: string; data?: unknown }): string => {
  if (res.type !== 'error' || typeof res.data !== 'object' || !res.data) {
    return FALLBACK_ERROR;
  }

  const messages = (res.data as { errors?: { message?: string }[] }).errors
    ?.map((e) => e.message)
    .filter(Boolean);

  return messages?.length ? messages.join(', ') : FALLBACK_ERROR;
};
